import { css } from 'styled-components';
import { buttonColors, buttonColorVariables } from './button.colors';
import { tagColors, tagColorVariables } from './tag.colors';

export const colors = {
  bg: {
    primary: 'var(--colour-role-bg-primary)',
    secondary: 'var(--colour-role-bg-secondary)',
    'feature-1': 'var(--colour-role-bg-feature-1)',
    'feature-2': 'var(--colour-role-bg-feature-2)',
    'feature-3': 'var(--colour-role-bg-feature-3)',
    'feature-4': 'var(--colour-role-bg-feature-4)',
    'feature-5': 'var(--colour-role-bg-feature-5)',
    info: 'var(--colour-role-bg-info)',
    success: 'var(--colour-role-bg-success)',
    warning: 'var(--colour-role-bg-warning)',
    error: 'var(--colour-role-bg-error)',
    interactive: 'var(--colour-role-bg-interactive)',
    disabled: 'var(--colour-role-bg-disabled)',
  },
  text: {
    primary: 'var(--colour-role-text-primary)',
    secondary: 'var(--colour-role-text-secondary)',
    invert: 'var(--colour-role-text-invert)',
    error: 'var(--colour-role-text-error)',
    success: 'var(--colour-role-text-success)',
    warning: 'var(--colour-role-text-warning)',
    info: 'var(--colour-role-text-info)',
    disabled: 'var(--colour-role-text-disabled)',
    interactive: 'var(--colour-role-text-interactive)',
    'interactive-hover': 'var(--colour-role-text-interactive-hover)',
    'interactive-invert': 'var(--colour-role-text-interactive-invert)',
    'interactive-invert-hover': 'var(--colour-role-text-interactive-invert-hover)',
    'interactive-invert-pressed': 'var(--colour-role-text-interactive-invert-pressed)',
  },
  border: {
    primary: 'var(--colour-role-border-primary)',
    secondary: 'var(--colour-role-border-secondary)',
    disabled: 'var(--colour-role-border-disabled)',
    invert: 'var(--colour-role-border-invert)',
    error: 'var(--colour-role-border-error)',
    success: 'var(--colour-role-border-success)',
    warning: 'var(--colour-role-border-warning)',
    info: 'var(--colour-role-border-info)',
    focus: 'var(--colour-role-border-focus)',
    interactive: 'var(--colour-role-border-interactive)',
    'box-shadow': 'var(--colour-role-border-box-shadow)',
  },
  icon: {
    primary: 'var(--colour-role-icon-primary)',
    secondary: 'var(--colour-role-icon-secondary)',
    invert: 'var(--colour-role-icon-invert)',
    interactive: 'var(--colour-role-icon-interactive)',
    disabled: 'var(--colour-role-icon-disabled)',
    info: 'var(--colour-role-icon-info)',
    success: 'var(--colour-role-icon-success)',
    warning: 'var(--colour-role-icon-warning)',
    error: 'var(--colour-role-icon-error)',
  },
  button: buttonColors,
  tag: tagColors,
  overlay: {
    background: 'var(--colour-components-overlay-background)',
  },
  'icon-heart': { active: 'var(--colour-role-icon-heart-active)' },
};

export const colorVariables = css`
  :root {
    --colour-brand-pure-white: #ffffff;
    --colour-brand-vivid-blue: #01d8ff;
    --colour-brand-bmo-blue: #0075be;
    --colour-brand-navy: #005587;
    --colour-brand-midnight: #002a52;
    --colour-brand-slate: #101518;
    --colour-brand-slate-rgb: 16, 21, 24;
    --colour-brand-bmo-red: #e91c24;
    --colour-brand-sky-blue-100: #eaf9ff;
    --colour-brand-sky-blue-200: #d4f3ff;
    --colour-brand-sky-blue-300: #bfeeff;
    --colour-brand-sky-blue-400: #aae8ff;
    --colour-brand-sky-blue-500: #94e2ff;
    --colour-brand-sky-blue-600: #7fdcff;
    --colour-brand-sky-blue-700: #69d6ff;
    --colour-brand-sky-blue-800: #54d1ff;
    --colour-brand-sky-blue-900: #3fcbff;
    --colour-brand-sky-blue-1000: #29c5ff;
    --colour-neutral-000: #ffffff;
    --colour-neutral-100: #f5f6f7;
    --colour-neutral-200: #ececee;
    --colour-neutral-300: #d9dce1;
    --colour-neutral-400: #929ba9;
    --colour-neutral-500: #646c76;
    --colour-neutral-600: #25303c;
    --colour-neutral-700: #1a252e;
    --colour-neutral-800: #101518;
    --colour-blue-100: #ecf7fe;
    --colour-blue-200: #c7eafc;
    --colour-blue-300: #9acdf9;
    --colour-blue-400: #51aef6;
    --colour-blue-500: #0e92f1;
    --colour-blue-600: #0075be;
    --colour-blue-700: #005587;
    --colour-blue-800: #002a52;
    --colour-red-100: #fff5f7;
    --colour-red-200: #fddde2;
    --colour-red-300: #faaeb9;
    --colour-red-400: #ed6e6e;
    --colour-red-500: #f71f27;
    --colour-red-600: #b51f08;
    --colour-red-700: #8b1f04;
    --colour-red-800: #5c0c00;
    --colour-yellow-100: #fffeeb;
    --colour-yellow-200: #fff7dc;
    --colour-yellow-300: #fee59a;
    --colour-yellow-400: #ffc414;
    --colour-yellow-500: #cd970f;
    --colour-yellow-600: #9c6a0a;
    --colour-yellow-700: #7d4e07;
    --colour-yellow-800: #553706;
    --colour-green-100: #f2feee;
    --colour-green-200: #d8f4d9;
    --colour-green-300: #b1e5ba;
    --colour-green-400: #69c981;
    --colour-green-500: #14a367;
    --colour-green-600: #128155;
    --colour-green-700: #086342;
    --colour-green-800: #0c4532;

    --colour-role-bg-primary: var(--colour-neutral-000);
    --colour-role-bg-secondary: var(--colour-brand-sky-blue-100);
    --colour-role-bg-feature-1: var(--colour-brand-sky-blue-200);
    --colour-role-bg-feature-2: var(--colour-brand-sky-blue-300);
    --colour-role-bg-feature-3: var(--colour-brand-sky-blue-500);
    --colour-role-bg-feature-4: var(--colour-brand-vivid-blue);
    --colour-role-bg-feature-5: var(--colour-brand-bmo-blue);
    --colour-role-bg-info: var(--colour-blue-100);
    --colour-role-bg-success: var(--colour-green-100);
    --colour-role-bg-warning: var(--colour-yellow-100);
    --colour-role-bg-error: var(--colour-red-100);
    --colour-role-bg-interactive: var(--colour-brand-bmo-blue);
    --colour-role-bg-disabled: var(--colour-neutral-200);

    --colour-role-text-primary: var(--colour-brand-slate);
    --colour-role-text-secondary: var(--colour-neutral-500);
    --colour-role-text-invert: var(--colour-neutral-000);
    --colour-role-text-error: var(--colour-red-500);
    --colour-role-text-success: var(--colour-green-500);
    --colour-role-text-warning: var(--colour-yellow-500);
    --colour-role-text-info: var(--colour-blue-500);
    --colour-role-text-disabled: var(--colour-neutral-400);
    --colour-role-text-interactive: var(--colour-brand-navy);
    --colour-role-text-interactive-hover: var(--colour-brand-midnight);
    --colour-role-text-interactive-invert: var(--colour-brand-sky-blue-100);
    --colour-role-text-interactive-invert-hover: var(--colour-brand-pure-white);
    --colour-role-text-interactive-invert-pressed: var(--colour-brand-pure-white);

    --colour-role-border-primary: var(--colour-neutral-300);
    --colour-role-border-secondary: var(--colour-neutral-500);
    --colour-role-border-disabled: var(--colour-neutral-200);
    --colour-role-border-invert: var(--colour-brand-pure-white);
    --colour-role-border-error: var(--colour-red-500);
    --colour-role-border-success: var(--colour-green-500);
    --colour-role-border-warning: var(--colour-yellow-500);
    --colour-role-border-info: var(--colour-blue-500);
    --colour-role-border-focus: var(--colour-blue-700);
    --colour-role-border-interactive: var(--colour-brand-bmo-blue);
    --colour-role-border-box-shadow: rgba(var(--colour-brand-slate-rgb), 0.1);

    --colour-role-icon-primary: var(--colour-brand-slate);
    --colour-role-icon-secondary: var(--colour-brand-midnight);
    --colour-role-icon-invert: var(--colour-brand-pure-white);
    --colour-role-icon-interactive: var(--colour-brand-navy);
    --colour-role-icon-disabled: var(--colour-neutral-400);
    --colour-role-icon-info: var(--colour-blue-500);
    --colour-role-icon-success: var(--colour-green-500);
    --colour-role-icon-warning: var(--colour-yellow-500);
    --colour-role-icon-error: var(--colour-red-500);

    --colour-role-icon-heart-active: var(--colour-brand-bmo-red);

    --colour-components-overlay-background: rgba(0, 42, 82, 0.80);

    ${buttonColorVariables}
    ${tagColorVariables}
  }
`;
